import KycMasterService from '../../services/KycMasterService';
import toast from 'react-hot-toast';

// Action Types
export const LOADING_TOGGLE_ACTION = '[KycMaster] Loading Toggle';
export const KYC_SETTING_LIST_INITIATED = '[KycMaster] KYC Setting List Initiated';
export const KYC_SETTING_INITIATED = '[KycMaster] KYC Setting Initiated';
export const KYC_SETTING_LIST_LOADED = '[KycMaster] KYC Setting List Loaded';
export const KYC_SETTING_LOADED = '[KycMaster] KYC Setting Loaded';
export const KYC_SETTING_FAILED = '[KycMaster] KYC Setting Failed';
export const KYC_SETTING_CREATED = '[KycMaster] KYC Setting Created';
export const KYC_SETTING_UPDATED = '[KycMaster] KYC Setting Updated';
export const KYC_SETTING_DELETED = '[KycMaster] KYC Setting Deleted';
export const CLEAR_CURRENT_KYC_SETTING = '[KycMaster] Clear Current KYC Setting';
export const CLEAR_ERRORS = '[KycMaster] Clear Errors';
export const SET_CURRENT_PAGE = '[KycMaster] Set Current Page';
export const SET_FILTERS = '[KycMaster] Set Filters';
export const RESET_KYC_SUCCESS = '[KycMaster] Reset Success State';

// Action Creators
export function loadingToggleAction(status) {
    return {
        type: LOADING_TOGGLE_ACTION,
        payload: status,
    };
}

export const loadKYCSettings = (params = {}) => {
    return (dispatch) => {
        dispatch({ type: KYC_SETTING_LIST_INITIATED });
        dispatch(loadingToggleAction(true));
        
        KycMasterService.getKYCSettings(params)
            .then((response) => {
                if (response.success) {
                    dispatch({ 
                        type: KYC_SETTING_LIST_LOADED, 
                        payload: {
                            data: response.data,
                            pagination: response.pagination
                        }
                    });
                } else {
                    dispatch({ type: KYC_SETTING_FAILED, payload: response.message });
                }
            })
            .catch((error) => {
                dispatch({ type: KYC_SETTING_FAILED, payload: error.message || 'Failed to load KYC settings' });
            })
            .finally(() => {
                dispatch(loadingToggleAction(false));
            });
    };
};

export const getKYCSettingById = (id) => {
    return (dispatch) => {
        dispatch({ type: KYC_SETTING_INITIATED });
        dispatch(loadingToggleAction(true));
        
        return KycMasterService.fetchKYCSettingById(id)
            .then((response) => {
                if (response.success) {
                    dispatch({ type: KYC_SETTING_LOADED, payload: response.data });
                    return response.data;
                } else {
                    dispatch({ type: KYC_SETTING_FAILED, payload: response.message });
                    throw new Error(response.message);
                }
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch KYC setting';
                dispatch({ type: KYC_SETTING_FAILED, payload: errorMessage });
                throw error;
            })
            .finally(() => {
                dispatch(loadingToggleAction(false));
            });
    };
};

export const removeKYCSettingById = (id) => {
    return (dispatch) => {
        dispatch({ type: KYC_SETTING_INITIATED });
        dispatch(loadingToggleAction(true));
        
        return KycMasterService.deleteKYCSettingById(id)
            .then((response) => {
                if (response.success) {
                    dispatch({ type: KYC_SETTING_DELETED, payload: id });
                    dispatch(loadKYCSettings()); // Refresh list after delete
                    toast.success(response.message || "KYC setting deleted successfully");
                    dispatch(resetKycSuccess());
                } else {
                    dispatch({ type: KYC_SETTING_FAILED, payload: response.message });
                    toast.error(response.message || "Failed to delete KYC setting");
                }
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to delete KYC setting';
                dispatch({ type: KYC_SETTING_FAILED, payload: errorMessage });
                toast.error(errorMessage);
            })
            .finally(() => {
                dispatch(loadingToggleAction(false));
            });
    };
};

export const saveKYCSettingAction = (data, isEdit, id, navigate) => {
    return (dispatch) => {
        dispatch({ type: KYC_SETTING_INITIATED });
        dispatch(loadingToggleAction(true));
        
        const apiCall = isEdit ? 
            KycMasterService.updateKYCSetting(id, data) : 
            KycMasterService.addKYCSetting(data);

        return apiCall
            .then((response) => {
                if (response.success) {
                    if (isEdit) {
                        dispatch({ type: KYC_SETTING_UPDATED, payload: response.data });
                    } else {
                        dispatch({ type: KYC_SETTING_CREATED, payload: response.data });
                    }
                    toast.success(response.message || `KYC setting ${isEdit ? 'updated' : 'created'} successfully`);
                    dispatch(resetKycSuccess());
                    if (navigate) {
                        navigate('/kyc-master');
                    }
                } else {
                    dispatch({ type: KYC_SETTING_FAILED, payload: response.message });
                    toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} KYC setting`);
                }
            })
            .catch((error) => {
                let errorMessage = 'An error occurred';
                
                if (error && error.response && error.response.data) {
                    errorMessage = error.response.data.message || 'Server error occurred';
                    dispatch({ type: KYC_SETTING_FAILED, payload: error.response.data });
                } else {
                    errorMessage = error.message || 'Network error occurred';
                    dispatch({ type: KYC_SETTING_FAILED, payload: errorMessage });
                }
                
                toast.error(errorMessage);
            })
            .finally(() => {
                dispatch(loadingToggleAction(false));
            });
    };
};

// Additional helper actions
export function clearCurrentKycSetting() {
    return {
        type: CLEAR_CURRENT_KYC_SETTING
    };
}

export function clearErrors() {
    return {
        type: CLEAR_ERRORS
    };
}

export function setCurrentPage(page) {
    return {
        type: SET_CURRENT_PAGE,
        payload: page
    };
}

export function setFilters(filters) {
    return {
        type: SET_FILTERS,
        payload: filters
    };
}

// New action creators for better API alignment
export const getKycMasterList = (params = {}) => {
    return loadKYCSettings(params);
};

export const createKycMaster = (data, navigate) => {
    return saveKYCSettingAction(data, false, null, navigate);
};

export const updateKycMaster = (id, data, navigate) => {
    return saveKYCSettingAction(data, true, id, navigate);
};

export const deleteKycMaster = (id) => {
    return removeKYCSettingById(id);
};

export const getKycMasterById = (id) => {
    return getKYCSettingById(id);
};

export const resetKycSuccess = () => ({
    type: RESET_KYC_SUCCESS
});